import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Loader2, RefreshCw, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * Unified activity feed powered by /api/activity.
 *
 * One component, three variants tuned for each role's shell:
 *
 *   variant="customer"    — 5 rows, timeline-style, friendly text.
 *   variant="installer"   — customer + project context per row.
 *   variant="operations"  — filterable feed (customer/project/engineer/
 *                            activity type / date range), paginated.
 *
 * The heavy filtering + pagination logic lives inside this component
 * so every future dashboard can drop it in with a `variant` prop and
 * inherit the full behaviour.
 *
 * Extra UX props (DEV-002A):
 *   initialLimit  — override the default page size (e.g. 5 rows for
 *                    installer/operations dashboards).
 *   collapsible   — render a chevron in the header. Collapsing keeps
 *                    fetched rows in memory (no reload on expand).
 */

const DEFAULT_LIMIT = { customer: 5, installer: 10, operations: 25 };

export default function ActivityFeed({
    variant = "installer",
    projectId = null,
    projectIds = null,
    customerId = null,
    engineerId = null,
    types = null,
    onViewAll = null,
    showFilters = false,
    testidPrefix = "activity-feed",
    initialLimit = null,
    collapsible = false,
}) {
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const limit = initialLimit ?? DEFAULT_LIMIT[variant] ?? 10;

    const load = useCallback(async (opts = {}) => {
        const isMore = !!opts.cursor;
        isMore ? setLoadingMore(true) : setLoading(true);
        try {
            const params = { limit };
            if (opts.cursor) params.cursor = opts.cursor;
            if (projectId) params.project_id = projectId;
            // `projectIds` (multi) scopes the feed to a caller-supplied set —
            // the Engineer Dashboard passes the current engineer's assigned
            // project ids so the feed shows activity for their work only.
            // An explicit empty array is honoured (feed will be empty).
            if (Array.isArray(projectIds)) params.project_ids = projectIds;
            if (customerId) params.customer_id = customerId;
            if (engineerId) params.engineer_id = engineerId;
            if (types && types.length) params.type = types;
            const r = await api.listActivity(params);
            setItems(isMore ? (prev) => [...prev, ...r.items] : r.items);
            setNextCursor(r.next_cursor || null);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load activity");
        } finally {
            setLoading(false); setLoadingMore(false);
        }
    }, [limit, projectId, projectIds, customerId, engineerId, types]);

    useEffect(() => { load(); }, [load]);

    const toggleCollapsed = () => setCollapsed((c) => !c);

    if (variant === "customer") {
        return (
            <CustomerCard
                items={items}
                loading={loading}
                onViewAll={onViewAll}
                testidPrefix={testidPrefix}
            />
        );
    }
    if (variant === "operations") {
        return (
            <OperationsFeed
                items={items}
                loading={loading}
                loadingMore={loadingMore}
                nextCursor={nextCursor}
                onLoadMore={() => nextCursor && load({ cursor: nextCursor })}
                onReload={() => load()}
                showFilters={showFilters}
                testidPrefix={testidPrefix}
                collapsible={collapsible}
                collapsed={collapsed}
                onToggleCollapsed={toggleCollapsed}
            />
        );
    }
    return (
        <InstallerCard
            items={items}
            loading={loading}
            loadingMore={loadingMore}
            nextCursor={nextCursor}
            onLoadMore={() => nextCursor && load({ cursor: nextCursor })}
            testidPrefix={testidPrefix}
            collapsible={collapsible}
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
        />
    );
}

/** Renders a lucide icon by name string; falls back gracefully. */
function LucideByName({ name, size = 14, strokeWidth = 1.75, className = "" }) {
    const Cmp = Icons[name] || Icons.CircleDot;
    return <Cmp size={size} strokeWidth={strokeWidth} className={className} />;
}

/** Common "X ago" formatter used by every variant. */
function relTime(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "";
    const diff = Date.now() - t;
    const s = Math.round(diff / 1000);
    if (s < 60)   return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60)   return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24)   return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 30)   return `${d}d ago`;
    const mo = Math.round(d / 30);
    return mo < 12 ? `${mo}mo ago` : `${Math.round(mo / 12)}y ago`;
}

/** Small chevron toggle used by collapsible variants. */
function CollapseToggle({ collapsed, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            aria-expanded={!collapsed}
            className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-[#E5E7EB] bg-white text-[#131B2F] hover:bg-[#F5F5F7]"
            title={collapsed ? "Expand" : "Collapse"}
        >
            {collapsed ? <ChevronDown size={14} strokeWidth={1.75} /> : <ChevronUp size={14} strokeWidth={1.75} />}
        </button>
    );
}


// ─── Customer variant ────────────────────────────────────────────────────

function CustomerCard({ items, loading, onViewAll, testidPrefix }) {
    return (
        <div className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid={`${testidPrefix}-customer`}>
            <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                <div className="text-sm font-semibold text-[#131B2F]">Recent Activity</div>
                {onViewAll && (
                    <button
                        type="button"
                        onClick={onViewAll}
                        data-testid={`${testidPrefix}-view-all`}
                        className="text-xs text-[#3E9E2E] hover:underline inline-flex items-center gap-1"
                    >
                        View full activity <ChevronRight size={12} />
                    </button>
                )}
            </div>
            {loading ? (
                <div className="p-8 text-center text-xs text-[#4B5565]"><Loader2 size={14} className="inline animate-spin" /> Loading…</div>
            ) : items.length === 0 ? (
                <div className="p-8 text-center text-xs text-[#4B5565]">Your project timeline will show here as it progresses.</div>
            ) : (
                <ul className="divide-y divide-[#F0F1F5]">
                    {items.slice(0, 5).map((e) => (
                        <li key={e.id} className="px-5 py-3.5 flex items-start gap-3" data-testid={`${testidPrefix}-row-${e.id}`}>
                            <div className="w-9 h-9 rounded-md bg-[#E6F1DE] text-[#2E7E22] flex items-center justify-center shrink-0">
                                <LucideByName name={e.icon} size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-[#131B2F]">{e.title}</div>
                                <div className="text-xs text-[#4B5565] mt-0.5 leading-relaxed">{e.description}</div>
                            </div>
                            <div className="text-[11px] text-[#4B5565] whitespace-nowrap pt-0.5">{relTime(e.timestamp)}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}


// ─── Installer variant ───────────────────────────────────────────────────

function InstallerCard({ items, loading, loadingMore, nextCursor, onLoadMore, testidPrefix, collapsible, collapsed, onToggleCollapsed }) {
    return (
        <div className="rounded-2xl border border-[#E5E7EB] bg-white" data-testid={`${testidPrefix}-installer`}>
            <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#131B2F]">Recent Activity</div>
                    <div className="text-[11px] text-[#4B5565]">Customer, project and task context for every event.</div>
                </div>
                {collapsible && (
                    <CollapseToggle
                        collapsed={collapsed}
                        onClick={onToggleCollapsed}
                        testid={`${testidPrefix}-collapse-toggle`}
                    />
                )}
            </div>
            {!collapsed && (
                <>
                    {loading ? (
                        <div className="p-8 text-center text-xs text-[#4B5565]"><Loader2 size={14} className="inline animate-spin" /> Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="p-8 text-center text-xs text-[#4B5565]">No activity yet.</div>
                    ) : (
                        <ul className="divide-y divide-[#F0F1F5]" data-testid={`${testidPrefix}-list`}>
                            {items.map((e) => <InstallerRow key={e.id} e={e} testidPrefix={testidPrefix} />)}
                        </ul>
                    )}
                    {!loading && nextCursor && (
                        <div className="p-3 border-t border-[#E5E7EB] text-center">
                            <button
                                type="button"
                                onClick={onLoadMore}
                                disabled={loadingMore}
                                data-testid={`${testidPrefix}-load-more`}
                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#131B2F] hover:bg-[#F5F5F7] disabled:opacity-60"
                            >
                                {loadingMore ? <Loader2 size={12} className="animate-spin" /> : null}
                                Load more
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function InstallerRow({ e, testidPrefix }) {
    const ctx = e.context || {};
    return (
        <li className="px-5 py-3 flex items-start gap-3" data-testid={`${testidPrefix}-row-${e.id}`}>
            <div className="w-9 h-9 rounded-md bg-[#E1E6F0] text-[#131B2F] flex items-center justify-center shrink-0">
                <LucideByName name={e.icon} size={16} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-medium text-[#131B2F] truncate">{e.title}</div>
                    <div className="text-[11px] text-[#4B5565] whitespace-nowrap">{relTime(e.timestamp)}</div>
                </div>
                <div className="text-xs text-[#4B5565] mt-0.5">{e.description}</div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-[#4B5565]">
                    {ctx.customer_name && <span><span className="text-[#131B2F] font-medium">Customer:</span> {ctx.customer_name}</span>}
                    {ctx.task_title && <span><span className="text-[#131B2F] font-medium">Task:</span> {ctx.task_title}</span>}
                    {ctx.appointment_title && <span><span className="text-[#131B2F] font-medium">Appointment:</span> {ctx.appointment_title}</span>}
                    {ctx.assignee_name && <span><span className="text-[#131B2F] font-medium">Engineer:</span> {ctx.assignee_name}</span>}
                </div>
            </div>
        </li>
    );
}


// ─── Operations variant (filterable) ─────────────────────────────────────

function OperationsFeed({ items, loading, loadingMore, nextCursor, onLoadMore, onReload, showFilters, testidPrefix, collapsible, collapsed, onToggleCollapsed }) {
    return (
        <div className="rounded-2xl border border-[#E5E7EB] bg-white" data-testid={`${testidPrefix}-operations`}>
            <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#131B2F]">Activity feed</div>
                    <div className="text-[11px] text-[#4B5565]">Every business event across the platform. Newest first.</div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onReload}
                        data-testid={`${testidPrefix}-reload`}
                        className="inline-flex items-center h-8 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                        title="Reload"
                    >
                        <RefreshCw size={12} strokeWidth={1.75} />
                    </button>
                    {collapsible && (
                        <CollapseToggle
                            collapsed={collapsed}
                            onClick={onToggleCollapsed}
                            testid={`${testidPrefix}-collapse-toggle`}
                        />
                    )}
                </div>
            </div>

            {!collapsed && (
                <>
                    {loading ? (
                        <div className="p-8 text-center text-xs text-[#4B5565]"><Loader2 size={14} className="inline animate-spin" /> Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="p-8 text-center text-xs text-[#4B5565]">No activity matches the current filters.</div>
                    ) : (
                        <ul className="divide-y divide-[#F0F1F5] max-h-[560px] overflow-y-auto" data-testid={`${testidPrefix}-ops-list`}>
                            {items.map((e) => <OperationsRow key={e.id} e={e} testidPrefix={testidPrefix} />)}
                        </ul>
                    )}
                    {!loading && nextCursor && (
                        <div className="p-3 border-t border-[#E5E7EB] text-center">
                            <button
                                type="button"
                                onClick={onLoadMore}
                                disabled={loadingMore}
                                data-testid={`${testidPrefix}-load-more`}
                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#131B2F] hover:bg-[#F5F5F7] disabled:opacity-60"
                            >
                                {loadingMore ? <Loader2 size={12} className="animate-spin" /> : null}
                                Load more
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function OperationsRow({ e, testidPrefix }) {
    const ctx = e.context || {};
    return (
        <li className="px-5 py-3 flex items-start gap-3" data-testid={`${testidPrefix}-row-${e.id}`}>
            <div className="w-8 h-8 rounded-md bg-[#F5F5F7] text-[#131B2F] flex items-center justify-center shrink-0">
                <LucideByName name={e.icon} size={14} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#131B2F]">{e.title}</span>
                    <span className="font-mono text-[10px] text-[#4B5565] bg-[#F5F5F7] px-1.5 py-0.5 rounded">{e.type}</span>
                </div>
                <div className="mt-0.5 text-xs text-[#4B5565]">{e.description}</div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-[#4B5565]">
                    <span title={e.timestamp}>{relTime(e.timestamp)}</span>
                    {ctx.customer_name && <span>· Customer: <span className="text-[#131B2F]">{ctx.customer_name}</span></span>}
                    {e.project_id && <span>· Project {String(e.project_id).slice(0, 8)}…</span>}
                    {ctx.assignee_name && <span>· Engineer: {ctx.assignee_name}</span>}
                    {ctx.engineer_name && <span>· Engineer: {ctx.engineer_name}</span>}
                </div>
            </div>
        </li>
    );
}
